/**
 * Route surface-broker actions, and keep them out of `onA2UIAction`'s body.
 *
 * That callback must keep a stable identity — `A2UISurfaceList` puts it in a
 * `MessageProcessor` useMemo dep list, and an unstable one rebuilds the
 * processor mid-edit and wipes in-progress surface input — so the branch there
 * stays three lines and the work lives here.
 *
 * Nothing in this file reaches the network. Apply, Preview and Dismiss are
 * short-circuited locally by design: they change the view the underwriter is
 * looking at, not the stored quote, so POSTing them would be both wrong and
 * unobservable.
 */
import type { A2UIClientAction, A2UIEnvelope } from '@cowbell/moo';
import {
  dispatchViewSurfaceAction,
  noteActionError,
  ownsAction,
} from '@cowbell/surface-broker';

const PREVIEW_ACTION = 'PreviewViewChanges';
const STAGE_ACTION = 'StageViewChanges';

/**
 * Actions the broker owns. Never POSTed to the surfaces/action endpoint.
 *
 * Reserved by NAME rather than left to `ownsAction`, because `ownsAction` reads
 * the currently registered surface — so an action fired after the view
 * unmounted, or while a different one is active, would fall through to the
 * network. For staging that is the difference between "open the form and I'll
 * put them there" and a request the backend has no handler for.
 */
const LOCAL_ONLY_ACTIONS = new Set([
  'ApplyViewChanges',
  'DismissViewChanges',
  // Fired by the hand-off card on mount, not by a click. Local for the same
  // reason as the rest: it changes the view the underwriter is looking at, not
  // the stored quote.
  STAGE_ACTION,
  PREVIEW_ACTION,
]);

export function isViewSurfaceAction(name: string): boolean {
  return LOCAL_ONLY_ACTIONS.has(name) || ownsAction(name);
}

export interface ViewSurfaceActionOutcome {
  readonly ok: boolean;
  readonly message?: string;
  readonly data?: Record<string, unknown> | null;
  readonly a2uiMessages?: readonly A2UIEnvelope[];
}

/**
 * A preview writes to `/preview/<id>`; everything else writes to
 * `/results/<id>`.
 *
 * A new surface gets no result plumbing for free — `settleGenerateResult` is
 * hard-gated on CopyQuote — so the console synthesises the envelope itself.
 * Feeding it back through `a2uiMessages` means it survives a processor rebuild,
 * unlike component-local state.
 */
function envelopeFor(action: A2UIClientAction, value: unknown): A2UIEnvelope[] {
  const path =
    action.name === PREVIEW_ACTION
      ? `/preview/${action.sourceComponentId}`
      : `/results/${action.sourceComponentId}`;

  return [
    {
      version: 'v0.9',
      updateDataModel: { surfaceId: action.surfaceId, path, value },
    } as unknown as A2UIEnvelope,
  ];
}

/**
 * A preview that did not produce a price still has to land on `/preview/<id>`,
 * in the shape the card reads.
 *
 * `ProposalPreview['status']` is `ok | invalid | failed` — there is no
 * `rejected`. Writing a refusal in the result shape here rendered the generic
 * "could not be priced" line and threw away the actual reason, which since
 * auto-preview is the common case (a stale revision, a read-only quote).
 */
function previewFailure(message?: string) {
  return {
    status: 'failed',
    detail: message ?? 'This combination could not be priced.',
  };
}

/**
 * A hand-off card's "Use $25,000" follow-up replaces the card's result, so a
 * failure stores the card it carried back, with the reason, rather than a
 * rejection a remounted card would render in place of rows on the quote.
 */
function failedFollowUp(
  action: A2UIClientAction,
  message: string
): ViewSurfaceActionOutcome | null {
  const carry = action.payload.carry as { card?: unknown } | undefined;
  if (
    action.name !== STAGE_ACTION ||
    typeof carry?.card !== 'object' ||
    carry.card === null
  ) {
    return null;
  }
  return {
    ok: true,
    a2uiMessages: envelopeFor(action, {
      ...(carry.card as Record<string, unknown>),
      followUpError: message,
      failedChangeSetId: action.payload.changeSetId,
    }),
  };
}

export async function handleViewSurfaceAction(
  action: A2UIClientAction
): Promise<ViewSurfaceActionOutcome> {
  const isPreview = action.name === PREVIEW_ACTION;

  try {
    const result = await dispatchViewSurfaceAction({
      name: action.name,
      viewKey:
        typeof action.payload.viewKey === 'string'
          ? action.payload.viewKey
          : '',
      payload: action.payload,
    });

    // A preview is not chat-worthy either way — it lands on the surface,
    // silently, and the card says whether there is a price.
    if (isPreview) {
      const preview = result.ok
        ? (result.data as { preview?: unknown } | undefined)?.preview
        : previewFailure(result.message);
      return { ok: true, a2uiMessages: envelopeFor(action, preview) };
    }

    if (!result.ok) {
      const kept = failedFollowUp(
        action,
        result.message ?? 'That change couldn’t be added.'
      );
      if (kept) {
        return kept;
      }
      // Reported ON the surface and NOT returned as `message`. Returning both
      // renders the identical sentence twice — once in the card's result row,
      // once as an assistant turn appended by `applyActionResult` — which is
      // what a refusal actually looked like in the panel. The card is the right
      // home: it is where the button the user pressed lives, and it is the only
      // place that can retire that button.
      return {
        ok: false,
        a2uiMessages: envelopeFor(action, {
          status: 'rejected',
          reason: result.reason,
          message: result.message,
        }),
      };
    }

    // No `message` here either. The card renders the outcome in its result
    // row, and `applyActionResult` appends any returned message as a separate
    // assistant turn — so "Applied 1 change. The premium is now $21,535."
    // showed up twice, once inside the card and once directly beneath it.
    //
    // Nothing is lost from the thread: the card IS part of the message, so an
    // append-only history still records what happened (D6).
    return {
      ok: true,
      data: result.data ?? null,
      a2uiMessages: envelopeFor(action, {
        ...(result.data ?? {}),
        message: result.message,
      }),
    };
  } catch (err) {
    // Never let a throw escape. The card disables both buttons while a preview
    // is in flight and waits for an envelope to re-enable them, so an
    // exception that produced no envelope would strand it: no price, no Apply,
    // no way back.
    //
    // Bound, not discarded. This catch previously wrote `catch {`, so the only
    // trace of a bug reaching it was a generic sentence on the card: no console
    // output, nothing in `surfaceBrokerStatus()`, and — because the preview
    // fallback text is byte-identical to the one the preview capability emits
    // when the RATER declines — no way to tell a code defect from a pricing
    // refusal. Recording it is what makes the next failure diagnosable.
    noteActionError(action.name, err);

    if (isPreview) {
      return {
        ok: true,
        a2uiMessages: envelopeFor(
          action,
          previewFailure(
            'Something went wrong while pricing this. Run __surfaceBroker.status() for the error.'
          )
        ),
      };
    }
    const message =
      'That action could not be completed. Run __surfaceBroker.status() for the error.';
    const kept = failedFollowUp(
      action,
      'That change couldn’t be added. Try again.'
    );
    if (kept) {
      return kept;
    }
    return {
      ok: false,
      a2uiMessages: envelopeFor(action, { status: 'rejected', message }),
    };
  }
}
